/* eslint-disable @typescript-eslint/ban-types */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { convertAreaCdsToDisplayNameArray } from '@functional';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import Button from '@ui-elements/Button';
import ConditionBox from '@ui-parts/ConditionBox';
import RegionModal from '@ui-parts/RegionModal';
import DetailedConditionsAreaSpecialPanelOne from '@ui-parts/DetailedConditionsAreaSpecialPanelOne';
import DetailedConditionsAreaSpecialPanelTwo from '@ui-parts/DetailedConditionsAreaSpecialPanelTwo';

import { ButtonProps } from '@material-ui/core';
import { SPECIAL_PANEL_DISPLAY_SETTING_IDS } from '~/constants';
import { PALETTE, FONT_SIZES, FONT_WEIGHTS } from '~/assets/styles/base';

const Wrapper = styled.div`
  & .MuiPaper-root {
    box-shadow: 0px 0px 0px;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  display: none;
`;

const DetailedConditionsWrapper = styled(AccordionDetails)`
  width: 100%;
  background-color: #f4f8fc;
  border: 1px solid #e8eff4;
  border-top: 0;
  padding: 20px 40px 20px;
`;

const Title = styled.div`
  padding: 0 5px;
  color: ${PALETTE.text.main};
  font-size: ${FONT_SIZES.title5};
`;

const FlexBox = styled(Box)`
  display: flex;
  align-items: center;
`;

const FlexItem = styled(Box)`
  flex-grow: 1;
  margin: 10px 0 0;
  padding: 0 5px;
`;

const ButtonArea = styled.div`
  padding: 0 20px;
`;

const DetailedConditionButton = styled(Button)`
  border-top: none;
  border-radius: 0 0 10px 10px;
  width: 108px;
  padding: 0;
  font-size: 1.2rem;
  font-weight: ${FONT_WEIGHTS.base};
  &.MuiButton-root {
    min-height: 23px;
  }
  &:hover {
    border-top: none;
    border-radius: 0 0 10px 10px;
  }
  span > .MuiSvgIcon-root {
    font-size: 1.2rem;
  }
`;

type Props = {
  areas: Array<{}>;
  checkedAreaCds: Array<string>;
  checkedItems: {};
  specialPanelDisplaySettingId: number;
};

const DetailedConditionsArea: React.FC<Props & ButtonProps> = ({
  areas,
  checkedAreaCds,
  checkedItems,
  specialPanelDisplaySettingId,
}): JSX.Element => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);
  const [openModals, setOpenModals] = useState({
    region: false,
  });

  const [selectedAreaNames, setSelectedAreaNames] = useState<any[]>([]);

  useEffect(() => {
    setSelectedAreaNames(convertAreaCdsToDisplayNameArray(areas, checkedAreaCds));
  }, [areas, checkedAreaCds]);

  return (
    <Wrapper data-testid="uiParts-DetailedConditionsArea">
      <Accordion expanded={expanded} disableGutters>
        <StyledAccordionSummary
          sx={{
            pointerEvents: 'none',
          }}
        />
        <DetailedConditionsWrapper data-testid="DetailedConditionsWrapper">
          <Title>{t('quotas.conditions.detailedConditionsTitle')}</Title>
          <FlexBox>
            <FlexItem>
              <ConditionBox
                conditionLabel={t('quotas.conditions.titles.region')}
                selectedChoices={selectedAreaNames}
                name="QuotaRegion"
                editable={selectedAreaNames.length > 0}
                handleClickAddBtn={() => {
                  setOpenModals({
                    ...openModals,
                    region: true,
                  });
                }}
                handleClickEditBtn={() => {
                  setOpenModals({
                    ...openModals,
                    region: true,
                  });
                }}
              />
            </FlexItem>
          </FlexBox>
          {(() => {
            if (specialPanelDisplaySettingId === SPECIAL_PANEL_DISPLAY_SETTING_IDS.V1) {
              return <DetailedConditionsAreaSpecialPanelOne checkedItems={checkedItems} />;
            }
            if (specialPanelDisplaySettingId === SPECIAL_PANEL_DISPLAY_SETTING_IDS.V2) {
              return <DetailedConditionsAreaSpecialPanelTwo checkedItems={checkedItems} />;
            }
            return null;
          })()}
        </DetailedConditionsWrapper>
      </Accordion>

      <RegionModal
        open={openModals.region}
        areas={areas}
        checkedAreaCdsProp={checkedAreaCds}
        handleClose={() => {
          setOpenModals({
            ...openModals,
            region: false,
          });
        }}
      />

      <ButtonArea>
        <DetailedConditionButton
          variant="contained"
          onClick={() => setExpanded(!expanded)}
          startIcon={expanded ? <RemoveCircleIcon /> : <AddCircleIcon />}
          data-testid="DetailedConditionButton"
        >
          {t('quotas.conditions.detailedConditions')}
        </DetailedConditionButton>
      </ButtonArea>
    </Wrapper>
  );
};

export default DetailedConditionsArea;
